import './VisibilityToggle.scss';

import React, { useCallback, useState } from 'react';

import ToggleSwitch from '../../../../common/ToggleSwitch';
import bem from '../../../../utils/bem';
import { useUpdateCustomFilter } from './hooks/useUpdateCustomFilter';
import type { CustomFilterVisibility } from './types';
import { CustomFilterVisibility as Visibility } from './types';

export interface VisibilityToggleProps {
  filterId: string;
  visibility: CustomFilterVisibility;
  filterPayload?: unknown;
  onVisibilityChanged?: (visibility: CustomFilterVisibility) => void;
  disabled?: boolean;
  skipMutation?: boolean;
}

const [block, element] = bem('visibility-toggle');

const VisibilityToggle = ({
  filterId,
  visibility,
  filterPayload,
  onVisibilityChanged,
  disabled = false,
  skipMutation = false,
}: VisibilityToggleProps): JSX.Element => {
  const { updateFilter, loading } = useUpdateCustomFilter();
  const [error, setError] = useState<string | null>(null);

  const isPublic = visibility === Visibility.PUBLIC;

  const handleToggle = useCallback(async () => {
    const newVisibility = isPublic ? Visibility.PRIVATE : Visibility.PUBLIC;
    setError(null);
    if (skipMutation) {
      onVisibilityChanged?.(newVisibility);
      return;
    }
    try {
      await updateFilter(filterId, {
        visibility: newVisibility,
        ...(filterPayload !== undefined && { filterPayload: filterPayload as never }),
      });
      onVisibilityChanged?.(newVisibility);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to update visibility';
      setError(message);
    }
  }, [isPublic, filterId, filterPayload, updateFilter, onVisibilityChanged, skipMutation]);

  return (
    <div className={block()} data-testid="applied-custom-filter-public-toggle">
      <span className={element('label')}>Make filter public</span>
      <ToggleSwitch isOn={isPublic} onChange={handleToggle} disabled={loading || disabled} />
      {error && (
        <div className={element('error')} data-testid="visibility-toggle-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default VisibilityToggle;
